// Per-event Video Intelligence settings and the sysadmin backfill.
//
// The config is `events/{eventId}/videoAI/config` rather than a field on the
// event document, because the event document is readable by any signed-in user
// and these settings are not.

import {
  collection, doc, getDoc, onSnapshot, query, serverTimestamp, setDoc,
  updateDoc, where,
} from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { auth, db, functions } from '../firebase'
import { DEFAULT_VIDEO_AI_CONFIG } from '../constants/videoAI'

const configRef = (eventId) => doc(db, 'events', eventId, 'videoAI', 'config')

/** An event's settings, falling back to the all-off default. */
export async function fetchVideoAIConfig(eventId) {
  const snap = await getDoc(configRef(eventId))
  if (!snap.exists()) return { ...DEFAULT_VIDEO_AI_CONFIG }
  const data = snap.data()
  return {
    ...DEFAULT_VIDEO_AI_CONFIG,
    ...data,
    features: { ...DEFAULT_VIDEO_AI_CONFIG.features, ...(data.features || {}) },
  }
}

export async function saveVideoAIConfig(eventId, config) {
  await setDoc(configRef(eventId), {
    enabled: config.enabled === true,
    features: config.features,
    aiModeration: config.aiModeration === true,
    explicitThreshold: config.explicitThreshold,
    updatedAt: serverTimestamp(),
    updatedBy: auth.currentUser ? auth.currentUser.uid : null,
  }, { merge: true })
}

// An analysis takes 30-60s, and retryVideoAnalysis waits for one. The callable
// client gives up after 70s by default, which aborts the request and reports
// failure while the function carries on and finishes — so the work succeeds
// and the UI says it failed. Wait as long as the function itself may run.
const CALLABLE_TIMEOUT_MS = 540000

/**
 * "Analyze existing videos". With `dryRun` it only counts and prices: how many
 * videos are missing a detection the saved settings ask for, and the seconds
 * owed per feature key. Otherwise it starts the event's background job and
 * returns as soon as the work is queued.
 * Sysadmin only — the callable enforces it.
 */
export async function runVideoAIBackfill({ eventId, dryRun = false, applyModeration = false }) {
  const call = httpsCallable(functions, 'backfillVideoAnalysis', { timeout: CALLABLE_TIMEOUT_MS })
  const res = await call({ eventId, dryRun, applyModeration })
  return res.data
}

/** Stop the event's running job; finished videos stay finished. */
export async function stopVideoAIBackfill(eventId) {
  const call = httpsCallable(functions, 'stopVideoAIBackfill')
  const res = await call({ eventId })
  return res.data
}

/**
 * The event's latest backfill job, live, with its progress summed from its
 * chunks the way subscribeExportProgress sums an export's. Calls back with
 * `null` when the event has never had one. Sysadmin-readable only.
 */
export function subscribeVideoAIBackfill(eventId, onChange) {
  const jobRef = doc(db, 'videoAIBackfills', eventId)
  let stopChunks = () => {}
  let runId = null
  let job = null
  let counts = { done: 0, failed: 0, analyzed: 0 }
  const emit = () => onChange(job ? { ...job, ...counts } : null)

  const stopJob = onSnapshot(jobRef, (snap) => {
    job = snap.exists() ? snap.data() : null
    if ((job?.runId ?? null) === runId) return emit()
    // A new run replaced the document: follow its chunks, not the old ones.
    stopChunks()
    runId = job?.runId ?? null
    counts = { done: 0, failed: 0, analyzed: 0 }
    emit()
    if (!runId) return
    const q = query(collection(jobRef, 'chunks'), where('runId', '==', runId))
    stopChunks = onSnapshot(q, (chunks) => {
      const next = { done: 0, failed: 0, analyzed: 0 }
      for (const d of chunks.docs) {
        for (const outcome of Object.values(d.data().outcomes || {})) {
          next.done++
          if (outcome === 'error') next.failed++
          if (outcome === 'done') next.analyzed++
        }
      }
      counts = next
      emit()
    }, (err) => console.warn('AI backfill progress subscription failed:', err.message))
  }, (err) => {
    console.warn('AI backfill subscription failed:', err.message)
    onChange(null)
  })

  return () => {
    stopJob()
    stopChunks()
  }
}

/**
 * Re-run the analysis for one video, discarding whatever it produced before.
 * Authorized to the event's admins by the callable; the videos page offers it
 * to sysadmins only.
 */
export async function retryVideoAnalysis(videoId) {
  const call = httpsCallable(functions, 'retryVideoAnalysis', { timeout: CALLABLE_TIMEOUT_MS })
  const res = await call({ videoId })
  return res.data
}

/**
 * Videos the AI rejected after a human had approved them, until acknowledged.
 * Videos never overridden have no `aiOverride` field and never match.
 */
export function subscribeAiOverrides(eventId, callback) {
  const q = query(
    collection(db, 'videos'),
    where('eventId', '==', eventId),
    where('aiOverride.acknowledged', '==', false)
  )
  return onSnapshot(q, (snapshot) => {
    callback(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })))
  }, (err) => {
    console.warn('AI override subscription failed:', err.message)
    callback([])
  })
}

export async function acknowledgeAiOverride(videoId) {
  await updateDoc(doc(db, 'videos', videoId), { 'aiOverride.acknowledged': true })
}
